import type {
  ConditionGroup,
  NextPageParams,
  OpportunitiesResponse,
  Opportunity,
  PipelineOpportunitiesResponse,
} from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"

export interface CreateOpportunityParams {
  name: string
  value?: number | null
  probability?: number | null
  target_close_date?: Date | null
  household_id?: string | null
  opportunityable_type?: "Household" | "Contact" | "Business" | null
  opportunityable_id?: string | null
  owner_id?: string | null
  pipeline_stage_id?: string
  description?: string | null
  custom_fields_attributes?: Record<string, unknown>
  tag_ids?: string[]
  book_id?: string
}

export interface UpdateOpportunityParams extends Partial<CreateOpportunityParams> {
  pipeline_id?: string
}

export async function fetchOpportunities(
  context: ApiContext,
  pipelineId: string,
  conditionGroup?: ConditionGroup,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  focusOpportunityId?: string | null,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  sumFieldKey?: string,
  pipelineStageId?: string
): Promise<PipelineOpportunitiesResponse> {
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.set("page", nextPageParams.page.toString())
    params.set("per_page", nextPageParams.per_page.toString())
  }

  if (conditionGroup) {
    appendConditionGroupParams(params, conditionGroup)
  }

  appendViewSortParams(params, viewSort)

  // Include a deep-linked opportunity even if it lives in a hidden stage
  if (focusOpportunityId) {
    params.set("focus_opportunity_id", focusOpportunityId)
  }

  if (searchTerm) {
    params.set("search", searchTerm)
  }

  if (sumFieldKey) {
    params.set("sum_field_key", sumFieldKey)
  }

  if (pipelineStageId) {
    params.set("pipeline_stage_id", pipelineStageId)
  }

  const queryString = params.toString()
  const url = queryString
    ? `/pipelines/${pipelineId}/opportunities?${queryString}`
    : `/pipelines/${pipelineId}/opportunities`

  return apiContextFetch(context, url)
}

export async function fetchOpportunity(
  context: ApiContext,
  pipelineId: string,
  opportunityId: string
): Promise<Opportunity> {
  return apiContextFetch(context, `/pipelines/${pipelineId}/opportunities/${opportunityId}`)
}

export async function createOpportunity(
  context: ApiContext,
  pipelineId: string,
  params: CreateOpportunityParams,
  overrideBookId?: string
): Promise<Opportunity> {
  return apiContextFetch(context, `/pipelines/${pipelineId}/opportunities`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ opportunity: params, override_book_id: overrideBookId }),
  })
}

export async function updateOpportunity(
  context: ApiContext,
  pipelineId: string,
  opportunityId: string,
  params: UpdateOpportunityParams
): Promise<Opportunity> {
  return apiContextFetch(context, `/pipelines/${pipelineId}/opportunities/${opportunityId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ opportunity: params }),
  })
}

export async function deleteOpportunity(context: ApiContext, pipelineId: string, opportunityId: string): Promise<void> {
  return apiContextFetch(context, `/pipelines/${pipelineId}/opportunities/${opportunityId}`, {
    method: "DELETE",
  })
}

export async function fetchHouseholdOpportunities(
  context: ApiContext,
  householdId: string
): Promise<OpportunitiesResponse> {
  return apiContextFetch(context, `/households/${householdId}/opportunities`)
}

export async function fetchContactOpportunities(
  context: ApiContext,
  contactId: string
): Promise<OpportunitiesResponse> {
  return apiContextFetch(context, `/contacts/${contactId}/opportunities`)
}

export async function fetchBusinessOpportunities(
  context: ApiContext,
  businessId: string
): Promise<OpportunitiesResponse> {
  return apiContextFetch(context, `/businesses/${businessId}/opportunities`)
}
